import { toValue } from 'vue'
import { set as _set, isObject as _isObject } from 'lodash'
import axios from '@/vendors/axios/axios'
import Paginator from '@/classes/paginators/Paginator'
import Query from '@/classes/queries/Query'

export default function actionsStore ({ store }) {
  store.$fetchItems = ({ endpoint, resource, model, multi_currency = false, query = {} }) => {
    const q = new Query({ query: { ...toValue(query), currency: multi_currency ? store.$currency : null } }).to_string
    const key = q || 'default'

    if (resource) {
      _set(store.$state, `${resource}.['${key}'].error`, null)
      _set(store.$state, `${resource}.['${key}'].status`, 'processing')
    }

    return new Promise((resolve, reject) => {
      axios.get(endpoint + q)
        .then(response => {
          const { data, paginator } = response.data
          const items = model ? new model(data) : data
          const pagination = paginator ? new Paginator(paginator) : null

          if (resource) {
            if (pagination) _set(store.$state, `${resource}.['${key}'].pagination`, pagination)

            _set(store.$state, `${resource}.['${key}'].items`, items)
            _set(store.$state, `${resource}.['${key}'].status`, 'success')
          }

          resolve({ items, pagination })
        })
        .catch(error => {
          if (resource) {
            _set(store.$state, `${resource}.['${key}'].error`, error)
            _set(store.$state, `${resource}.['${key}'].status`, 'failure')
          }

          reject(error)
        })
    })
  }

  store.$fetchItem = async ({ endpoint, resource, model, multi_currency = false, query = {} }) => {
    const q = new Query({ query: { ...toValue(query), currency: multi_currency ? store.$currency : null } }).to_string

    if (resource) {
      _set(store.$state, `${resource}.error`, null)
      _set(store.$state, `${resource}.status`, 'processing')
      _set(store.$state, `${resource}.currency`, store.$currency)
    }

    return new Promise((resolve, reject) => {
      axios.get(endpoint + q)
        .then(response => {
          const item = model ? new model(response.data.data) : response.data.data

          if (resource) {
            _set(store.$state, `${resource}.item`, item)
            _set(store.$state, `${resource}.status`, 'success')
          }

          resolve(item)
        })
        .catch(error => {
          if (resource) {
            _set(store.$state, `${resource}.error`, error)
            _set(store.$state, `${resource}.status`, 'failure')
          }

          reject(error)
        })
    })
  }

  store.$postItem = ({ endpoint, resource, model, params }) => {
    _set(store.$state, 'loader', true)
    _set(store.$state, 'error', null)

    return new Promise((resolve, reject) => {
      axios.post(endpoint, params)
        .then(response => {
          const item = model ? new model(response.data.data) : response.data.data
          const regex = /(\w+)\.\{([\w-]+)\}/
          const [, attribute, key] = regex.exec(resource) || []
          const id = key && item[key] ? `${attribute}.${item[key]}` : resource
          if (id) _set(store.$state, `${id}.item`, item)

          resolve(item)
        })
        .catch(error => {
          _set(store.$state, 'error', error)

          reject(error)
        })
        .finally(() => _set(store.$state, 'loader', false))
    })
  }

  store.$fetchFile = ({ url }) => {
    if (!url) return Promise.reject()

    _set(store.$state, 'loader', true)
    _set(store.$state, 'error', null)

    return new Promise((resolve, reject) => {
      axios.get(url, { responseType: 'blob' })
        .then(({ data }) => resolve(data))
        .catch(error => {
          _set(store.$state, 'error', error)

          reject(error)
        })
        .finally(() => _set(store.$state, 'loader', false))
    })
  }

  store.$postFile = ({ endpoint, params }) => {
    _set(store.$state, 'loader', true)
    _set(store.$state, 'error', null)

    return new Promise((resolve, reject) => {
      axios.post(endpoint, { filename: params.name })
        .then(response => {
          axios.put(response.data.data.upload_url, params.binary, {
            headers: { 'Content-Type': params.type },
            transformRequest: [(data, headers) => {
              delete headers.Authorization
              return data
            }]
          })
            .then(() => resolve(response.data.data.file_url))
            .catch(error => {
              _set(store.$state, 'error', error)

              reject(error)
            })
            .finally(() => _set(store.$state, 'loader', false))
        })
        .catch(error => {
          _set(store.$state, 'error', error)

          reject(error)
        })
    })
  }

  store.$patchItem = ({ endpoint, resource, model, params, config = {} }) => {
    if (resource) {
      _set(store.$state, `${resource}.error`, null)
      _set(store.$state, `${resource}.status`, 'processing')
    }

    return new Promise((resolve, reject) => {
      axios.patch(endpoint, params, config)
        .then(response => {
          const item = model ? new model(response.data.data) : response.data.data

          if (resource) {
            _set(store.$state, `${resource}.item`, item)
            _set(store.$state, `${resource}.status`, 'success')
          }

          resolve(item)
        })
        .catch(error => {
          if (resource) {
            _set(store.$state, `${resource}.error`, error)
            _set(store.$state, `${resource}.status`, 'failure')
          }

          reject(error)
        })
    })
  }

  store.$deleteItem = ({ endpoint, resource }) => {
    _set(store.$state, 'loader', true)
    _set(store.$state, 'error', null)

    return new Promise((resolve, reject) => {
      axios.delete(endpoint)
        .then(() => {
          if (resource) {
            Object.keys(store.$state).forEach(attribute => {
              const state = store.$state[attribute]
              if (_isObject(state) && (resource in state)) delete state[resource]
            })
          }

          resolve()
        })
        .catch(error => {
          _set(store.$state, 'error', error)

          reject(error)
        })
        .finally(() => _set(store.$state, 'loader', false))
    })
  }

  store.$fetchExport = ({ endpoint, query, filename }) => {
    const q = new Query({ query: toValue(query) }).to_string

    _set(store.$state, 'loader', true)
    _set(store.$state, 'error', null)

    return new Promise((resolve, reject) => {
      axios.get(endpoint + q, { responseType: 'blob' })
        .then(response => {
          const link = document.createElement('a')
          link.href = URL.createObjectURL(response.data)
          link.setAttribute('download', filename)
          link.click()

          URL.revokeObjectURL(link.href)

          resolve()
        })
        .catch(error => {
          _set(store.$state, 'error', error)

          reject(error)
        })
        .finally(() => _set(store.$state, 'loader', false))
    })
  }
}